import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from '../../../shared/DatePicker'
import CustomButton from '../../../shared/CustomButton'
import moment from 'moment'
import { fetchData } from '../../../helpers/fetchData'
import toast from 'react-hot-toast'
import { FaCheckDouble } from 'react-icons/fa6'
import DropdownField from '../../../shared/DropdownField'
import BulkProcessingLogs from './BulkProcessingLogs'

function BulkSubmitClaimsModal({ onClose, onComplete }) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    start_date: moment().startOf('month').format('YYYY-MM-DD'),
    end_date: moment().endOf('month').format('YYYY-MM-DD'),
    max_workers: 5,
    payer_id: ''
  })
  const [loading, setLoading] = useState(false)
  const [payerList, setPayerList] = useState(null)
  const [fieldLoading, setFieldLoading] = useState(false)
  const [jobId, setJobId] = useState(null)

  const getPayersList = async () => {
    setFieldLoading(true)
    let response = await fetchData('POST', '/v3/payer/get', {})
    response?.data ? setPayerList(response?.data) : setPayerList(null)
    setFieldLoading(false)
  }

  useEffect(() => {
    getPayersList()
  }, [])

  const handleDateChange = (event) => {
    const { name, value } = event.target || {}
    setFormData(prev => ({
      ...prev,
      [name]: moment(value).format('YYYY-MM-DD')
    }))
  }

  const handlePayerChange = (e) => {
    setFormData(prev => ({
      ...prev,
      payer_id: e.target?.value
    }))
  }

  const handleSubmit = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.end_date).isBefore(moment(formData.start_date))) {
      toast.error(t('End date must be after start date'))
      return
    }

    setLoading(true)
    try {
      const payload = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        max_workers: formData.max_workers,
        payer_id: formData.payer_id,
        include_failed: true
      }

      const response = await fetchData('POST', 'v3/invoices/sendBulkClaims', payload)
      if(response?.message && !response?.job_id){
        toast.error(response?.message)
      }
      if (response?.job_id) {
        toast.success(response?.message || t('Bulk submission started successfully'))
        setJobId(response.job_id)
      }
    } catch (error) {
      console.error('Bulk submission error:', error)
      toast.error(error?.message || t('Failed to start bulk submission'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {jobId ? (
        <BulkProcessingLogs jobId={jobId} onClose={onClose} onComplete={onComplete} />
      ) : (
      <div className='space-y-2'>
        <div className='flex  items-center gap-4'>
          <DatePicker
            label={t('StartDate')}
            name='start_date'
            value={moment(formData.start_date, 'YYYY-MM-DD').toDate()}
            onChange={handleDateChange}
            useFormik={false}
            placeholder={t('StartDate')}
          />
          <DatePicker
            label={t('EndDate')}
            name='end_date'
            value={moment(formData.end_date, 'YYYY-MM-DD').toDate()}
            onChange={handleDateChange}
            useFormik={false}
            placeholder={t('EndDate')}
          />
        </div>
        <div>
          <DropdownField
            filter
            useFormik={false}
            options={payerList?.map(py => ({
              value: py?.payer_id,
              label: py?.name
            }))}
            loading={fieldLoading}
            label={t('Payer')}
            placeholder={t('Select Payer')}
            handleChange={handlePayerChange}
            value={formData?.payer_id}
          />
        </div>

        <div className='flex gap-2 justify-end pt-2'>
          <CustomButton
            label={t('Start Processing')}
            buttonType='primary'
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
            icon={<FaCheckDouble size={15} className='me-2' />}
          />
        </div>
      </div>
      )}
    </div>
  )
}

export default BulkSubmitClaimsModal
